import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";

import { useMyContext } from "../../../components/Provider/CommonProvider";
import AdminDeleteModal from "../../../components/CommonComponents/AdminDeleteModal";
import AdminNavbar from "../../../components/CommonComponents/AdminNavbar";
import { toast } from "react-toastify";
import Response_Processing from "../../../layout/Others/Response_Processing";
import MessageBox from "../../../components/CommonComponents/MessageBox";
import NoRecordTable from "../../../components/CommonComponents/NoRecordTable";
import { FaEllipsisVertical } from "react-icons/fa6";
import { IconDeleteFigma, IconEditFigma } from "../../../Assets/MyIcons";
import { donwloadCSV, downloadPDF, downloadXLSX } from "../../../components/CommonMethod/CommonMethods";
import { IoClose } from 'react-icons/io5';
import { RxReload } from "react-icons/rx";
import ExportIcon from "../../../components/Images/icons/Export_Icon_01.png";
import AdminTooltip from "../../../components/CommonComponents/AdminTooltip";

function RegionList() {
  const baseURL = process.env.REACT_APP_Baseurl;

  const indexApi = `${baseURL}/admin/region/index`;
  const deleteApi = `${baseURL}/admin/region/multi-delete`;
  const shuffleApi = `${baseURL}/admin/region/order`;

  const { fetchRequest } = useMyContext();
  const [withOutRefresh, setWithOutRefresh] = useState(false);
  const [apiData, setApiData] = useState();
  const [showOverlay, setShowOverlay] = useState(false);
  const [loading, setLoading] = useState(true);
  const [modalClose, setModelClose] = useState(false);
  const [selectAllData, setSelectAllData] = useState(false);
  const [itemDelete, setItemDelete] = useState([]);
  const [filteredData, setFilteredData] = useState();
  const [filter, setFilter] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);

  const handleFilter = (event) => {
    const searchTerm = event.target.value.toLowerCase();
    setFilter(searchTerm);
    if (searchTerm.trim() === '') {
      setFilteredData(apiData);
      return;
    }
    const filteredResults = apiData?.filter((item) =>
      ["id", "name"].some((property) =>
        typeof item[property] === "string" &&
        item[property].toLowerCase().includes(searchTerm)
      ) ||
      (typeof item.id === "number" && item.id.toString().includes(searchTerm))
    );
    setFilteredData(filteredResults);
  };

  const handleClear = () => {
    setFilter('');
    setFilteredData(apiData);
  };

  const fetchData = async (type) => {
    setShowOverlay(true);
    setLoading(true);
    try {
      const response = await fetchRequest({
        method: 'GET',
        url: `${indexApi}`,
        params: {},
        data: {},
      });
      if (response?.status) {
        const updatedList = response?.Region;
        setApiData(updatedList);
        setFilteredData(updatedList);
        if (type === "CSV") {
          await donwloadCSV(updatedList, "REGION-LIST");
        }
        if (type === "PDF") {
          await downloadPDF(updatedList, "REGION-LIST", ["S.NO", "ID", "NAME", "SLUG"]);
        }
        if (type === "XLSX") {
          await downloadXLSX(updatedList, "REGION-LIST", ["S.NO", "ID", "NAME", "SLUG"]);
        }
      }
    } catch (error) {
      toast.error(error?.response?.data?.message);
    } finally {
      setShowOverlay(false);
      setLoading(false);
    }
  };

  const reloadAction = () => {
    setWithOutRefresh(prev => !prev);
  };

  const handleDelete = async () => {
    setShowOverlay(true);
    setIsDeleting(true);
    try {
      const response = await fetchRequest({
        method: 'DELETE',
        url: `${deleteApi}`,
        params: {},
        data: { id: [...itemDelete] },
      });
      const result = response;
      if (result?.status) {
        toast.success(result?.message);
        setWithOutRefresh(!withOutRefresh);
        setSelectAllData(false);
      }
    } catch (err) {
      toast.error(err?.response?.data?.message);
    } finally {
      setLoading(false);
      setShowOverlay(false);
      setIsDeleting(false);
      handleCloseModal();
    }
  };

  const saveShuffleOrder = async (updatedItems) => {
    setShowOverlay(true);
    if (!updatedItems || updatedItems?.length === 0) {
      toast.error("Region items are empty");
      return;
    }
    const ids = updatedItems?.map((item) => item?.id);
    const positions = updatedItems?.map((_, index) => index + 1);
    const requestData = { id: ids, position: positions };
    try {
      const response = await fetchRequest({
        method: 'POST',
        url: `${shuffleApi}`,
        params: {},
        data: requestData,
      });
      const result = response;
      if (result?.status) {
        toast.success(result?.message);
        setWithOutRefresh(!withOutRefresh);
        setShowOverlay(false);
      } else {
        setShowOverlay(false);
        toast.error(result?.message);
      }
    } catch (err) {
      setShowOverlay(false);
      toast.error(err?.response?.data?.message);
    }
  };

  const handleDragEnd = (result) => {
    if (!result.destination) return;
    const reorderedItems = Array.from(apiData);
    const [movedItem] = reorderedItems?.splice(result.source.index, 1);
    reorderedItems?.splice(result.destination.index, 0, movedItem);
    setApiData(reorderedItems);
    saveShuffleOrder(reorderedItems);
  };

  const handleSelectAllData = () => {
    setSelectAllData(!selectAllData);
    setItemDelete(selectAllData ? [] : apiData?.map((item) => item?.id));
  };

  const handleSingleFile = (itemId) => {
    if (itemDelete?.includes(itemId)) {
      setItemDelete(itemDelete?.filter((id) => id !== itemId));
    } else {
      setItemDelete([...itemDelete, itemId]);
    }
  };

  const handleCloseModal = () => {
    setModelClose(!modalClose);
    setItemDelete([]);
  };

  const handleOpenDeleteModal = async () => {
    setModelClose(true);
  };

  const closeModal = () => {
    setModelClose(!closeModal);
    setItemDelete([]);
    setSelectAllData(false);
  };

  const singleDelete = (id) => {
    setModelClose(true);
    setItemDelete([id]);
  };

  useEffect(async () => {
    setLoading(true);
    await fetchRequest({
      method: 'GET',
      url: `${indexApi}`,
      params: {},
      data: {},
    })
      .then(response => {
        if (response?.status) {
          setApiData(response?.Region);
          setFilteredData(response?.Region);
          setLoading(false);
        }
      })
      .catch((error) => {
        setLoading(false);
        toast.error(error?.response?.data?.message);
      })
      .finally(() => {
        setShowOverlay(false);
        setLoading(false);
      });
  }, [withOutRefresh]);

  return (
    <>
      {modalClose && (
        <AdminDeleteModal
          modelClose={modalClose}
          deleteClose={closeModal}
          deleteAction={handleDelete}
          buttonText={'custom'}
          buttonSubmitLoading={isDeleting}
          message={itemDelete?.length > 1 ? "Are you sure you want to delete the selected regions?" : "Are you sure you want to delete this region?"}
        />
      )}
      <AdminNavbar
        heading="Region Management"
        totalDataLength={apiData?.length}
        deleteCount={itemDelete?.length}
        navigation={'/admin-add-region'}
        deleteMethod={handleOpenDeleteModal}
        navText={'Add Region'}
      />
      <div className="bootstrapTable theme-bg-color p-3 rounded-2">
        <div className='d-flex align-items-center flex-wrap justify-content-between mb-3'>
          <div className="filterContainer">
            <input
              type='text'
              placeholder="Search"
              className="filter form-control theme-text-color theme-border-secondary"
              value={filter}
              onChange={(event) => handleFilter(event)}
            />
            {filter?.length > 0 && (
              <button type="button" className="clearBtn" onClick={() => handleClear()}>
                <IoClose className="custom-close-btn" />
              </button>
            )}
          </div>
          <div className='d-flex align-items-center'>
            <button
              id="reloadButton"
              className={`custom-tool-tip-parent bg-transparent reloadButton theme-text-color p-2 me-2 border border-2 rounded-circle theme-border-color`}
              onClick={reloadAction}
            >
              <RxReload style={{ width: "18px", height: "15px" }} className={`${loading ? "rotate-load" : ""}`} />
              <AdminTooltip text="Refresh" top={"-41px"} className="d-none custom-tool-tip" />
            </button>
            <div className="dropdown d-block ms-2" key={"2"}>
              <button
                className="btn btn-success dropdown-toggle d-flex hideArrow"
                type="button"
                id="exportAllButton"
                data-bs-toggle="dropdown"
                aria-expanded="false"
              >
                <span className="fw-bolder text-white">Export All</span>
                <img src={ExportIcon} className="ms-2 exportIcon" alt="exportIcon" />
              </button>
              <ul className="dropdown-menu theme-bg-color" aria-labelledby="exportAllButton">
                <li className="dropdown-item theme-text-color cursor-pointer" onClick={() => fetchData("CSV")}>CSV</li>
                <li className="dropdown-item theme-text-color cursor-pointer" onClick={() => fetchData("PDF")}>PDF</li>
                <li className="dropdown-item theme-text-color cursor-pointer" onClick={() => fetchData("XLSX")}>XLSX</li>
              </ul>
            </div>
          </div>
        </div>
        <div>
          <div className="row mx-3 theme-border-bottom-secondary">
            <div className="rowCheckboxDrag language-table col-3">
              <label><input type="checkbox" checked={selectAllData} onChange={handleSelectAllData} /></label>
            </div>
            <div className="col-4 justify-content-start language-table">
              <label className="theme-text-color fs-6">Region</label>
            </div>
            <div className="col-4 d-flex justify-content-start language-table">
              <label className="theme-text-color fs-6">Slug</label>
            </div>
            <div className="col-1 d-flex justify-content-center language-table">
              <label className="theme-text-color fs-6 language-table">Actions</label>
            </div>
          </div>
          <DragDropContext onDragEnd={handleDragEnd}>
            <Droppable droppableId="region-items" className="theme-bg-color">
              {(provided) => (
                <ul {...provided.droppableProps} ref={provided.innerRef} className="pb-5 px-0 theme-bg-color m-0 p-0 list-unstyled">
                  {loading ? (
                    <NoRecordTable result={filteredData?.length} loader={true} />
                  ) : filteredData?.length ? (
                    filteredData?.map((item, index) => (
                      <Draggable key={item?.id.toString()} draggableId={item?.id.toString()} index={index}>
                        {(provided) => (
                          <li
                            ref={provided.innerRef}
                            {...provided.draggableProps}
                            {...provided.dragHandleProps}
                            className={`theme-bg-color theme-text-color w-100 theme-border-bottom-secondary px-3`}
                          >
                            <div className="row m-0 p-0 align-items-center">
                              <div className="col-3 rowCheckboxDrag category-order-title">
                                <input
                                  type="checkbox"
                                  checked={itemDelete?.includes(item.id)}
                                  onChange={() => handleSingleFile(item.id)}
                                />
                              </div>
                              <div className="col-4 category-order-title">
                                <div className="d-flex justify-content-between align-items-center">
                                  {item?.region_image && (
                                    <img src={item?.region_image} className="profile" alt="region_image" />
                                  )}
                                  <div className="rightVideoTitle d-flex flex-column ms-2">
                                    <MessageBox
                                      text={item?.name?.length > 15 ? `${item.name.slice(0, 15)}...` : item?.name}
                                      loadingHide
                                    />
                                    <span className="dateText table-subheading">{item?.upload_on}</span>
                                  </div>
                                </div>
                              </div>
                              <div className="col-4 d-flex justify-content-start">
                                <div className="rightVideoTitle d-flex flex-column">
                                  <MessageBox
                                    text={item?.slug?.length > 15 ? `${item?.slug.slice(0, 15)}...` : item?.slug}
                                    loadingHide
                                  />
                                </div>
                              </div>
                              <div className="col-1 category-order-title d-flex justify-content-center">
                                <div className="editdropdown">
                                  <span className="editdropdown-button">
                                    <FaEllipsisVertical className="theme-text-color" />
                                  </span>
                                  <div className="editdropdown-menu theme-bg-color">
                                    <Link to={`/admin-edit-region/${item?.id}`} className="theme-text-color theme-bg-color">
                                      <IconEditFigma styled={{ width: "24px", height: "24px" }} />
                                      <span className="theme-text-color">
                                        <MessageBox text="Edit" classname='ms-2 theme-text-color' />
                                      </span>
                                    </Link>
                                    <div onClick={() => singleDelete(item?.id)} className="commonActionPadding theme-bg-color">
                                      <IconDeleteFigma styled={{ width: "24px", height: "24px" }} />
                                      <MessageBox text="Delete" classname='ms-2 theme-text-color' />
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </li>
                        )}
                      </Draggable>
                    ))
                  ) : (
                    <NoRecordTable result={apiData?.length} loader={false} />
                  )}
                </ul>
              )}
            </Droppable>
          </DragDropContext>
        </div>
      </div>
    </>
  );
}

export default RegionList;
